import { ComponentFactoryResolver, ComponentRef, Injectable, Injector } from '@angular/core';

import { asyncScheduler, fromEvent, Subject, Subscription, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  ComponentPortal,
  ComponentType,
  OverlayRef,
  OverlayService,
  OverlayState,
  PortalInjector,
} from '../core/core.module';
import { NotificationBodyBase } from './notification-body-base';
import { NotificationBodyComponent } from './notification-body/notification-body.component';
import { NotificationConfig, NotificationPosition, NotificationStyle } from './notification-config';
import { NotificationData } from './notification-data';
import { NotificationHostBase } from './notification-host-base';
import { NotificationHostComponent } from './notification-host/notification-host.component';
import { NotificationRef } from './notification-ref';
import { NotificationsContainerComponent } from './notifications-container/notifications-container.component';
import { NotificationsPositionStrategy } from './notifications-position-strategy';

interface AutoCloseController {
  createdAt: number | null;
  remainingTime: number;
  closeSubscription: Subscription | null;
  start: () => void;
  pause: () => void;
}

export type NotificationType = 'success' | 'error' | 'progress' | 'timeout';

const INNER_HTML_NODE = document.createElement('div');

@Injectable()
export class NotificationsController {
  private containers: Map<NotificationPosition, NotificationsContainerComponent> = new Map();
  private groupIdentifiers: string[] = [];
  private autoCloseRefs: AutoCloseController[] = [];

  constructor(
    private _overlay: OverlayService,
    private _injector: Injector,
    private cmpFactoryResolver: ComponentFactoryResolver,
  ) {}

  public success(
    data: NotificationData | string,
    config: NotificationConfig = {},
  ): NotificationRef {
    return this.notify(
      {
        ...config,
        style: NotificationStyle.Success,
        charactersCount: this.resolveCharactersCount(data),
      },
      data,
    );
  }

  public error(data: NotificationData | string, config: NotificationConfig = {}): NotificationRef {
    return this.notify(
      {
        ...config,
        style: NotificationStyle.Error,
        charactersCount: this.resolveCharactersCount(data),
      },
      data,
    );
  }

  public progress(
    data: NotificationData | string,
    config: NotificationConfig = {},
  ): NotificationRef {
    return this.notify(
      {
        ...config,
        style: NotificationStyle.InProgress,
      },
      data,
    );
  }

  public timeout(
    data: NotificationData | string,
    config: NotificationConfig = {},
  ): NotificationRef {
    return this.notify(
      {
        ...config,
        style: NotificationStyle.Timeout,
      },
      data,
    );
  }

  public notify<NotificationBody extends NotificationBodyBase = NotificationBodyComponent>(
    notificationConfig?: NotificationConfig,
    notificationData?: NotificationData | string,
    notificationComponent: ComponentType<NotificationBody> = <any>NotificationBodyComponent,
  ): NotificationRef<NotificationBody> {
    return <NotificationRef<NotificationBody>>(
      this.create(
        notificationComponent,
        NotificationHostComponent,
        notificationConfig,
        typeof notificationData === 'string' ? { title: notificationData } : notificationData,
      )
    );
  }

  public custom<T extends { containerComponent: NotificationsContainerComponent }>(
    notificationComponent: ComponentType<T>,
  ): ComponentRef<T> {
    const notificationInjector = new PortalInjector(this._injector);
    const notificationPortal = new ComponentPortal(
      notificationComponent,
      null,
      notificationInjector,
    );
    const target = this.attachNotificationsContainer({
      position: NotificationPosition.BottomRight,
    });

    const notificationRef = target.attachComponentPortal(notificationPortal, 0);

    notificationRef.changeDetectorRef.detectChanges();
    notificationRef.instance.containerComponent = target;

    return notificationRef;
  }

  private create<
    NotificationBody extends NotificationBodyBase,
    NotificationHost extends NotificationHostBase,
  >(
    notificationComponent: ComponentType<NotificationBody>,
    hostComponent: ComponentType<NotificationHost>,
    notificationConfig?: NotificationConfig,
    notificationData?: NotificationData,
  ): NotificationRef<NotificationBody, NotificationHost> | void {
    try {
      const config: NotificationConfig = { ...new NotificationConfig(), ...notificationConfig };

      const target: NotificationsContainerComponent = this.attachNotificationsContainer(config);

      const notificationRef = new NotificationRef<NotificationBody, NotificationHost>();
      notificationRef.config = config;

      const notificationInjector = this.createNotificationInjector(
        notificationRef,
        this._injector,
        notificationData,
      );
      const notificationPortal = new ComponentPortal(hostComponent, null, notificationInjector);

      if (config.groupIdentifier) {
        const groupIdentifierAlreadyExists = this.groupIdentifiers.includes(config.groupIdentifier);

        if (groupIdentifierAlreadyExists) {
          target.remove(config.groupIdentifier);
        } else {
          this.groupIdentifiers.push(config.groupIdentifier);
        }
      }
      const notificationHost = target.attachComponentPortal(
        notificationPortal,
        0,
        config.groupIdentifier,
      );
      notificationHost.changeDetectorRef.detectChanges();

      const contentPortal = new ComponentPortal(notificationComponent, null, notificationInjector);
      const notificationContent = notificationHost.instance.attachComponentPortal(contentPortal);

      notificationHost.onDestroy(() => this.removeNotification(notificationRef));

      notificationRef.bodyRef = notificationContent;
      notificationRef.hostRef = notificationHost;
      notificationRef.host = target;

      if (config.timeout! > 0) {
        const timeout = this.calculateTimeout(notificationRef.config);
        this.scheduleAutoClose(notificationRef, notificationHost, timeout);
      }

      return notificationRef;
    } catch (err) {
      // TODO: properly handle error (remove overlay & container);
      console.error(err);
    }
  }

  private scheduleAutoClose(
    notificationRef: NotificationRef,
    notificationHost: ComponentRef<any>,
    timeout: number,
  ): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const notificationsController = this;
    const destroyAutoClose$ = new Subject();
    const autoCloseController: AutoCloseController = {
      remainingTime: timeout,
      createdAt: null,
      closeSubscription: null,
      pause(): void {
        this.remainingTime = this.remainingTime - (Date.now() - this.createdAt!);
        this.closeSubscription!.unsubscribe();
      },
      start(): void {
        this.createdAt = Date.now();
        this.closeSubscription = timer(this.remainingTime, asyncScheduler).subscribe(() => {
          destroyAutoClose$.next();
          destroyAutoClose$.complete();
          notificationRef.close();
          notificationsController.autoCloseRefs = notificationsController.autoCloseRefs.filter(
            ref => ref !== autoCloseController,
          );
        });
      },
    };
    this.autoCloseRefs = [...this.autoCloseRefs, autoCloseController];

    autoCloseController.start();
    fromEvent(notificationHost.location.nativeElement, 'mouseenter')
      .pipe(takeUntil(destroyAutoClose$))
      .subscribe(() => this.autoCloseRefs.forEach(ref => ref.pause()));
    fromEvent(notificationHost.location.nativeElement, 'mouseleave')
      .pipe(takeUntil(destroyAutoClose$))
      .subscribe(() => this.autoCloseRefs.forEach(ref => ref.start()));
  }

  private createNotificationInjector(
    notificationRef: NotificationRef,
    userInjector: Injector,
    notificationData: any,
  ): PortalInjector {
    const customTokens = new WeakMap();

    customTokens.set(NotificationRef, notificationRef);
    customTokens.set(NotificationData, notificationData);
    customTokens.set(ComponentFactoryResolver, this.cmpFactoryResolver);

    return new PortalInjector(userInjector, customTokens);
  }

  private attachNotificationsContainer(
    config: NotificationConfig,
  ): NotificationsContainerComponent {
    if (this.containers.has(config.position!)) {
      return this.containers.get(config.position!)!;
    }

    const viewContainer = config ? config.viewContainerRef : null;
    const containerPortal = new ComponentPortal(NotificationsContainerComponent, viewContainer);

    const overlayRef = this.createOverlay(config.position!);
    const containerRef: ComponentRef<NotificationsContainerComponent> =
      overlayRef.attach(containerPortal);

    this.containers.set(config.position!, containerRef.instance);

    return containerRef.instance;
  }

  private removeNotification(ref: NotificationRef): void {
    const count = ref.host._portalHost.viewContainerRef.length;
    if (count === 0) {
      setTimeout(() => {
        ref.hostRef.destroy();
      });
    }
  }

  private createOverlay(position: NotificationPosition): OverlayRef {
    const overlayState = new OverlayState();
    const positionStrategy = new NotificationsPositionStrategy();
    overlayState.hasBackdrop = false;
    overlayState.disablePointerEvents = true;
    overlayState.zIndex = 9999;
    overlayState.level = 10000;

    switch (position) {
      case NotificationPosition.TopLeft:
        positionStrategy.top('70px');
        positionStrategy.left('0');
        break;

      case NotificationPosition.Top:
        positionStrategy.top('70px');
        positionStrategy.centerHorizontally('0');
        break;

      case NotificationPosition.TopRight:
        positionStrategy.top('70px');
        positionStrategy.right('0');
        break;

      case NotificationPosition.BottomRight:
        positionStrategy.bottom('0');
        positionStrategy.right('0');
        break;

      case NotificationPosition.Bottom:
        positionStrategy.bottom('0');
        positionStrategy.centerHorizontally('0');
        break;

      case NotificationPosition.BottomLeft:
        positionStrategy.bottom('0');
        positionStrategy.left('0');
        break;

      default:
        break;
    }
    overlayState.positionStrategy = positionStrategy;

    return this._overlay.create(overlayState);
  }

  private resolveCharactersCount(data: string | NotificationData): number {
    if (typeof data === 'string') return data.length;

    const { message, messageHTML, title } = data;
    let concatenatedText = '';
    if (message) concatenatedText = concatenatedText + message;
    if (title) concatenatedText = concatenatedText + title;
    if (messageHTML) {
      // eslint-disable-next-line no-restricted-properties
      INNER_HTML_NODE.innerHTML = messageHTML;
      concatenatedText = concatenatedText + INNER_HTML_NODE.innerText!;
    }

    return concatenatedText.replace(/ /g, '').length;
  }

  private calculateTimeout(config: NotificationConfig): number {
    const { timeout, charactersCount } = config;
    return (
      (!charactersCount ? timeout : this.getTimeoutByCharactersCount(charactersCount)) ||
      config.timeout!
    );
  }

  private getTimeoutByCharactersCount(charactersCount: number): number {
    const timeoutPerCharacter = 200;
    const maxTimeout = 8000;
    const totalTimeout = charactersCount * timeoutPerCharacter;

    return totalTimeout <= maxTimeout ? totalTimeout : maxTimeout;
  }
}
